import React, {
  type ComponentProps,
  createContext,
  type ReactNode,
  useContext,
  useMemo,
} from 'react'

import type { ReactButtonProps } from '../../types'
import { warnInDev } from '../../util/logging'
import { typeSafeObjectEntries } from '../../util/typeSafeObjectEntries'
import type { SemanticColorToken } from '../themes'
import { CssVars, NeutralColorToken, type ThemeColorToken } from '../themes'
import { Button } from './Button'
import { IconButton } from './IconButton'
import { InternalImage } from './internal/InternalImage'
import { InternalModal } from './internal/InternalModal'
import { Pog, type TextOrPictogram } from './Pog'
import { getColorValue, useTheme } from './ThemeProvider'
import { Type } from './Type'
import { useUniqueId } from './useUniqueId'
import { CSSInterpolation } from '@emotion/css/create-instance'

export type ModalWidth =
  | 'narrow'
  | 'standard'
  | 'wide'
  | 'extraWide'
  | 'extraExtraWide'

const MODAL_WIDTH_TO_VARIANT: Record<
  ModalWidth,
  ComponentProps<typeof InternalModal>['variant']
> = {
  extraExtraWide: 'xxl',
  extraWide: 'xl',
  narrow: 'narrow',
  standard: 'medium',
  wide: 'wide',
}

const ModalContext = createContext<
  | {
      width: Props['width']
      onClose: Props['onClose']
      titleId: string
      backgroundColor?: SemanticColorToken | ThemeColorToken
      foregroundColor?: SemanticColorToken | ThemeColorToken
    }
  | undefined
>(undefined)
function useModalContext(componentName: string) {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error(
      `Modal: Missing context provider. Are you using ${componentName} outside Modal?`
    )
  }
  return context
}
export function useModalTitleId() {
  return useModalContext('your custom header').titleId
}

type Props = {
  /**
   * The width of the resulting modal.
   */
  width: ModalWidth
  /**
   * Called when the user tries to close the modal in any of the following ways:
   * clicking on a rendered `X` button, pressing the `Esc` key, or clicking
   * outside the modal. If function is not provided, the `X` will not be rendered,
   * and you should handle closing the modal by some other means.
   */
  onClose?: ComponentProps<typeof InternalModal>['onClose']
  /**
   * The contents to render inside the modal.
   */
  children: ReactNode
  /**
   * The ARIA `role` to put on the modal. Defaults to `dialog`. Use `alertdialog` when you
   * the modal is high-priority and you want a screenreader to instantly start reading out
   * the contents.
   */
  role?: ComponentProps<typeof InternalModal>['role']
  /**
   * Pass true to avoid locking the user's tab focus inside the modal. You almost certainly
   * don't want to do this; focus lock is very important for accessibility.
   */
  _experimentalIsFocusLockDisabled?: ComponentProps<
    typeof InternalModal
  >['_experimentalIsFocusLockDisabled']
  /**
   * Optionally specify a custom color to use for the background of your modal. All
   * `Modal`-related components will adopt this color as appropriate, but you'll need to apply
   * the color to your other modal children as needed.
   */
  backgroundColor?: SemanticColorToken | ThemeColorToken
  /**
   * Optionally specify a custom color to use for the foreground of your modal (e.g. text and
   * buttons). All `Modal`-related components will adopt this color as appropriate, but you'll
   * need to apply the color to your other modal children as needed.
   */
  foregroundColor?: SemanticColorToken | ThemeColorToken

  overrideModalStyles?: CSSInterpolation
}

/**
 * Renders a modal built according to
 * [WAI-ARIA Authoring Practices 1.1](https://www.w3.org/TR/wai-aria-practices/#dialog_modal).
 * You should use the `Modal.Header`, `Modal.Illustration`, and `Modal.Footer` helper
 * components to render those parts of the contents of your modal.
 *
 * If you choose not to use `Modal.Header` or `Modal.Footer` for some reason, there are a few
 * things you'll need to keep in mind for accessibility:
 *
 * * You should use a `<header>` and `<footer>` for those parts of your `Modal`, if present.
 * * You'll need to use `useModalTitleId()` to get the ID string that needs to be attached
 *      to the element that contains the text title of your `Modal`. This ensures that the
 *      `Modal` has a title that can be found by screen readers.
 * * The title of your `Modal` should be in an `<h1>` element.
 */
export function Modal({
  width,
  children,
  onClose,
  overrideModalStyles,
  role = 'dialog',
  _experimentalIsFocusLockDisabled = false,
  backgroundColor = NeutralColorToken.Bg1,
  foregroundColor = NeutralColorToken.Fg1,
}: Props) {
  const titleId = useUniqueId()
  const theme = useTheme()
  const backgroundColorValue = getColorValue(backgroundColor, theme)

  const contextValue = useMemo(
    () => ({
      backgroundColor,
      foregroundColor,
      onClose,
      titleId,
      width,
    }),
    [backgroundColor, foregroundColor, onClose, titleId, width]
  )

  return (
    <ModalContext.Provider value={contextValue}>
      <InternalModal
        _experimentalIsFocusLockDisabled={_experimentalIsFocusLockDisabled}
        aria-labelledby={titleId}
        onClose={onClose}
        overrideModalStyles={[
          {
            backgroundColor: backgroundColorValue,
            padding: `16px calc(2 * ${CssVars.size.gutterWidth}) calc(2 * ${CssVars.size.gutterWidth})`,
          },
          overrideModalStyles
        ]}
        role={role}
        variant={MODAL_WIDTH_TO_VARIANT[width]}
      >
        {children}
      </InternalModal>
    </ModalContext.Provider>
  )
}

type ModalHeaderProps =
  | {
      children?: never
      title: string
      pogProps?: TextOrPictogram & {
        foregroundColorOverride?: ComponentProps<
          typeof Pog
        >['foregroundColorOverride']
        backgroundColorOverride?: ComponentProps<
          typeof Pog
        >['backgroundColorOverride']
      }
    }
  | {
      children: ReactNode
      pogProps?: never
      title?: never
    }

/**
 * Renders the header for a modal, optionally with a Pog as a visual header.
 *
 * This is only directly exported for tests; use it off of `Modal` instead.
 */
export function ModalHeader({ children, pogProps, title }: ModalHeaderProps) {
  const context = useModalContext('Modal.Header')

  if (pogProps) {
    if (isAtLeastExtraWide(context.width)) {
      warnInDev(
        'Modals that are at least extra-wide should not use Pogs as header visual. Please use a narrower Modal or the standard header style.'
      )
    }

    return (
      <header
        css={[
          {
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
          },
          // If we don't have a close button header, provide a bit more visual spacing between
          // the Pog and the top of the Modal.
          !context.onClose && {
            marginTop: '8px',
          },
        ]}
      >
        {context.onClose && (
          <IconButton
            aria-label="Close"
            backgroundColor={context.backgroundColor}
            foregroundColor={context.foregroundColor}
            icon="Close24"
            onClick={context.onClose}
            overrideRootStyles={{
              // Override the `align-items: center` from the parent so that the IconButton is
              // right-aligned.
              alignSelf: 'flex-end',

              // Pull the IconButton to the right a bit to better align the icon visual with
              // the right edge of other things in the modal.
              marginRight: '-8px',
            }}
            type="button"
          />
        )}
        <Pog {...pogProps} size="hero" />
        <Type.Text
          color={context.foregroundColor}
          element="h1"
          isBold
          maxLines={2}
          overrideRootStyles={{
            margin: `calc(2 * ${CssVars.size.gutterWidth}) 0 ${CssVars.size.gutterWidth}`,
            textAlign: 'center',
          }}
          size="L"
        >
          <span id={context.titleId}>{title}</span>
        </Type.Text>
      </header>
    )
  }

  return (
    <header
      css={{
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: CssVars.size.gutterWidth,
      }}
    >
      {children ?? (
        <Type.Text
          color={context.foregroundColor}
          element="h1"
          isBold
          maxLines={2}
          size="L"
        >
          <span id={context.titleId}>{title}</span>
        </Type.Text>
      )}
      {context.onClose && (
        <IconButton
          aria-label="Close"
          backgroundColor={context.backgroundColor}
          foregroundColor={context.foregroundColor}
          icon="Close24"
          onClick={context.onClose}
          overrideRootStyles={{
            // Override the `align-items: center` from the parent so that the IconButton is
            // always aligned to the top, even when the modal title spans multiple lines.
            alignSelf: 'flex-start',

            // Provide a bit of space between the close button and longer titles.
            marginLeft: CssVars.size.gutterWidth,

            // Pull the IconButton to the right a bit to better align the icon visual with the
            // right edge of other things in the modal.
            marginRight: '-8px',
          }}
          type="button"
        />
      )}
    </header>
  )
}
ModalHeader.displayName = 'Modal.Header'
Modal.Header = ModalHeader

/**
 * Renders a full bleed illustration for a modal.
 *
 * This is only directly exported for tests; use it off of `Modal` instead.
 */
export function ModalIllustration({
  dangerouslySetHeight,
  ...restProps
}: ComponentProps<typeof InternalImage> & {
  alt: string
  dangerouslySetHeight?: React.CSSProperties['height']
}) {
  const context = useModalContext('Modal.Illustration')

  if (context.width === 'extraExtraWide') {
    warnInDev(
      'Modals that are at least extra-wide should not use Illustrations. Please use a narrower Modal.'
    )
  }

  return (
    <Modal.FullBleed>
      <InternalImage
        {...restProps}
        css={{
          height: dangerouslySetHeight ?? 230,
          margin: `0 auto calc(2 * ${CssVars.size.gutterWidth})`,
          objectFit: 'cover',
          objectPosition: 'center',
          width: '100%',
        }}
      />
    </Modal.FullBleed>
  )
}
ModalIllustration.displayName = 'Modal.Illustration'
Modal.Illustration = ModalIllustration

/**
 * Renders the children full bleed in the modal.
 *
 * This is only directly exported for tests; use it off of `Modal` instead.
 */
export function ModalFullBleed({
  children,
}: {
  /**
   * The children that you wish to be rendered full bleed inside your modal.
   */
  children: ReactNode
}) {
  // We don't actually use any values from the context here; we just use it to enforce that
  // we're inside a Modal.
  useModalContext('Modal.FullBleed')

  return (
    <div css={{ margin: `0 calc(-2 * ${CssVars.size.gutterWidth})` }}>
      {children}
    </div>
  )
}
ModalFullBleed.displayName = 'Modal.FullBleed'
Modal.FullBleed = ModalFullBleed

/**
 * Renders the footer for a modal, containing either one or two buttons for the user to take
 * action.
 *
 * This is only directly exported for tests; use it off of `Modal` instead.
 */
export function ModalFooter({
  isHorizontalLayout = false,
  primaryButton,
  secondaryButton,
}: {
  primaryButton: {
    label: ComponentProps<typeof Button>['label']
    onClick: ReactButtonProps['onClick']
    state?: ComponentProps<typeof Button>['state']
    type?: 'button' | 'submit' | 'reset'
  }
  secondaryButton?: {
    label: ComponentProps<typeof Button>['label']
    onClick: ReactButtonProps['onClick']
    state?: ComponentProps<typeof Button>['state']
    type?: 'button' | 'submit' | 'reset'
  }
  isHorizontalLayout?: boolean
}) {
  const context = useModalContext('Modal.Footer')
  const shouldDisplayAsRow =
    isAtLeastExtraWide(context.width) || isHorizontalLayout
  const horizontalButtonStyles = isHorizontalLayout
    ? { flex: 1, minWidth: 0 }
    : undefined

  return (
    <footer
      css={[
        {
          marginTop: `calc(2 * ${CssVars.size.gutterWidth})`,
        },
        shouldDisplayAsRow && {
          display: 'flex',
          flexDirection: 'row-reverse',
          justifyContent: 'flex-start',
        },
      ]}
    >
      <Button
        {...primaryButton}
        backgroundColor={context.foregroundColor}
        foregroundColor={context.backgroundColor}
        overrideRootStyles={horizontalButtonStyles}
        type={primaryButton.type || 'button'}
        width={!isAtLeastExtraWide(context.width) && 'full'}
      />
      {secondaryButton && (
        <Button
          {...secondaryButton}
          backgroundColor={context.foregroundColor}
          overrideRootStyles={
            shouldDisplayAsRow
              ? {
                  marginRight: CssVars.size.gutterWidth,
                  ...horizontalButtonStyles,
                }
              : {
                  marginTop: CssVars.size.gutterWidth,
                }
          }
          type={secondaryButton.type || 'button'}
          variant="secondary"
          width={!isAtLeastExtraWide(context.width) && 'full'}
        />
      )}
    </footer>
  )
}
ModalFooter.displayName = 'Modal.Footer'
Modal.Footer = ModalFooter

function isAtLeastExtraWide(width: ModalWidth) {
  return width === 'extraWide' || width === 'extraExtraWide'
}

const MODAL_VARIANT_TO_WIDTH = typeSafeObjectEntries(
  MODAL_WIDTH_TO_VARIANT
).reduce(
  (acc, [width, variant]) => ({
    ...acc,
    [variant]: width,
  }),
  {} as Record<ComponentProps<typeof InternalModal>['variant'], ModalWidth>
)
/**
 * An adapter for the old Modal API, useful when transitioning everyone from the old Modal to
 * the new Modal.
 *
 * // TODO(eric.lovett): Switch all uses of this adapter to Modal and remove this.
 *
 * This is only directly exported for tests; use it off of `Modal` instead.
 *
 * @deprecated Use `Modal`, `Modal.Header`, and `Modal.Footer` instead.
 */
export function LegacyModalAdapter({
  _experimentalIsFocusLockDisabled = false,
  children,
  illustration,
  onClose,
  role = 'dialog',
  title,
  variant,
}: {
  title: string
  onClose?: ComponentProps<typeof InternalModal>['onClose']
  children: ReactNode
  variant: ComponentProps<typeof InternalModal>['variant']
  role?: ComponentProps<typeof InternalModal>['role']
  illustration?: {
    src: string
    src2x?: string
    src3x?: string
    alt: string
  }
  _experimentalIsFocusLockDisabled?: ComponentProps<
    typeof InternalModal
  >['_experimentalIsFocusLockDisabled']
}) {
  return (
    <Modal
      _experimentalIsFocusLockDisabled={_experimentalIsFocusLockDisabled}
      onClose={onClose}
      role={role}
      width={MODAL_VARIANT_TO_WIDTH[variant]}
    >
      <Modal.Header title={title} />
      {illustration && <Modal.Illustration {...illustration} />}
      <div>{children}</div>
    </Modal>
  )
}
LegacyModalAdapter.displayName = 'Modal.LegacyAdapter'
Modal.LegacyAdapter = LegacyModalAdapter
